import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Calendar, ChevronLeft, ChevronRight, RefreshCw, Edit2, Trash2, BarChart3, Users, Settings, ClipboardList, Target, ChevronDown, ChevronUp, ChevronRight as ChevronRightIcon, XCircle, Download, Clock } from 'lucide-react';
import { format, startOfWeek, addWeeks, subWeeks, getISOWeek, getYear } from 'date-fns';
import { exportToExcel } from '@/utils/exportToExcel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  planned: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  partially_completed: 'bg-amber-100 text-amber-700 border-amber-200',
  rolled_over: 'bg-orange-100 text-orange-700 border-orange-200',  // legacy fallback
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200'
};

export default function ProductionPlanningPage() {
  const { user, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('planning');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [plans, setPlans] = useState([]);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [filterSupervisor, setFilterSupervisor] = useState('all');
  const [filterWorker, setFilterWorker] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProcess, setFilterProcess] = useState('all');
  const [viewBy, setViewBy] = useState('planned');
  const [processSchedule, setProcessSchedule] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null); // Single expanded plan (accordion style)
  
  // Form state for new process-centric planning
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [selectedWOs, setSelectedWOs] = useState({}); // { wo_id|process_id: { checked, planned_qty } }
  const [supervisorId, setSupervisorId] = useState('');
  const [workerIds, setWorkerIds] = useState([]);
  const [workerSearch, setWorkerSearch] = useState('');
  
  // Enhancement 2: Process Dependency Warnings
  const [dependencyWarnings, setDependencyWarnings] = useState([]);
  const [checkingDependencies, setCheckingDependencies] = useState(false);

  // Phase 1 (Step 14): Cancel dialog state
  const [cancelPlan, setCancelPlan] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // F3 — Pending Processes tab state
  const [pendingSubTab, setPendingSubTab] = useState('so_target'); // 'so_target' | 'benchmark'
  const [pendingData, setPendingData] = useState({ with_date: [], without_date: [] });
  const [pendingLoading, setPendingLoading] = useState(false);
  const [ppFromDate, setPpFromDate] = useState('');
  const [ppToDate, setPpToDate] = useState('');
  const [ppProcessFilter, setPpProcessFilter] = useState('all');
  const [ppCustomerFilter, setPpCustomerFilter] = useState('all');
  const [ppSoFilter, setPpSoFilter] = useState('all');
  const [ppItemFilter, setPpItemFilter] = useState('all');
  const [ppCustomers, setPpCustomers] = useState([]);
  const [ppSalesOrders, setPpSalesOrders] = useState([]);
  const [ppItems, setPpItems] = useState([]);
  const [ppProcessMasters, setPpProcessMasters] = useState([]);
  const [expandedProcessKey, setExpandedProcessKey] = useState(null); // accordion

  // Get current week info
  const weekNumber = getISOWeek(currentDate);
  const weekYear = getYear(currentDate);
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
  const saturday = new Date(weekStart);
  saturday.setDate(saturday.getDate() + 5);

  const fetchData = async () => {
    try {
      const [plansRes, processesRes, employeesRes] = await Promise.all([
        axios.get(`${API_URL}/production-plans`, {
          params: { week_number: weekNumber, week_year: weekYear },
          headers: getAuthHeader()
        }),
        axios.get(`${API_URL}/production-plans/available-processes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/employees`, { headers: getAuthHeader() })
      ]);
      setPlans(plansRes.data);
      setAvailableProcesses(processesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch data');
    }
  };

  const fetchWeeklyReport = async () => {
    try {
      const params = { week_number: weekNumber, week_year: weekYear };
      if (filterSupervisor !== 'all') params.supervisor_id = filterSupervisor;
      if (filterWorker !== 'all') params.worker_id = filterWorker;
      
      const response = await axios.get(`${API_URL}/production-plans/weekly-report`, {
        params,
        headers: getAuthHeader()
      });
      setWeeklyReport(response.data);
    } catch (error) {
      console.error('Report error:', error);
    }
  };

  const fetchProcessSchedule = async () => {
    try {
      const params = { 
        week_number: weekNumber, 
        week_year: weekYear,
        view_by: viewBy
      };
      if (filterSupervisor !== 'all') params.supervisor_id = filterSupervisor;
      if (filterWorker !== 'all') params.worker_id = filterWorker;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterProcess !== 'all') params.process_name = filterProcess;
      
      const response = await axios.get(`${API_URL}/production-plans/process-schedule`, {
        params,
        headers: getAuthHeader()
      });
      setProcessSchedule(response.data);
    } catch (error) {
      console.error('Process schedule error:', error);
    }
  };

  // F3 — Pending Processes: fetch groups + lazy-load filter option lists.
  const fetchPendingProcesses = async () => {
    setPendingLoading(true);
    try {
      const params = {
        date_axis: pendingSubTab,
        include_no_date: pendingSubTab === 'benchmark',
      };
      if (ppFromDate) params.from_date = ppFromDate;
      if (ppToDate) params.to_date = ppToDate;
      if (ppProcessFilter !== 'all') params.process_id = ppProcessFilter;
      if (ppCustomerFilter !== 'all') params.customer_id = ppCustomerFilter;
      if (ppSoFilter !== 'all') params.sales_order_id = ppSoFilter;
      if (ppItemFilter !== 'all') params.item_id = ppItemFilter;
      const resp = await axios.get(`${API_URL}/production-plans/pending-processes`, {
        params,
        headers: getAuthHeader(),
      });
      setPendingData(resp.data || { with_date: [], without_date: [] });
    } catch (error) {
      console.error('Pending processes fetch error:', error);
      toast.error('Failed to fetch pending processes');
      setPendingData({ with_date: [], without_date: [] });
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchPendingFilterOptions = async () => {
    // One-shot: customers, SOs, items, process masters. Reused by all sub-tabs.
    try {
      const [clientsResp, sosResp, itemsResp, procResp] = await Promise.all([
        axios.get(`${API_URL}/clients`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/sales-orders`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/items`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/process-masters`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setPpCustomers(Array.isArray(clientsResp.data) ? clientsResp.data : (clientsResp.data?.data || []));
      setPpSalesOrders(Array.isArray(sosResp.data) ? sosResp.data : (sosResp.data?.data || []));
      setPpItems(Array.isArray(itemsResp.data) ? itemsResp.data : (itemsResp.data?.data || []));
      setPpProcessMasters(Array.isArray(procResp.data) ? procResp.data : (procResp.data?.data || []));
    } catch (error) {
      console.error('Pending processes filter options fetch error:', error);
    }
  };

  const handleExportPendingProcesses = () => {
    const rows = [];
    const pushGroup = (groups, fallbackDate) => {
      for (const g of groups || []) {
        for (const w of g.work_orders || []) {
          rows.push({
            'Process Name': g.process_name || '',
            'WO Number': w.wo_number || '',
            'Item Name': w.item_name || '',
            'Pending Qty': w.pending_qty ?? 0,
            'Required Date': w.required_date || fallbackDate || '',
            'Customer': w.customer_name || '',
            'SO Number': w.so_number || '',
          });
        }
      }
    };
    pushGroup(pendingData.with_date || [], '');
    // Without-date rows only populated on benchmark sub-tab.
    pushGroup(pendingData.without_date || [], '');
    if (rows.length === 0) {
      toast.info('Nothing to export');
      return;
    }
    const axis = pendingSubTab;
    const from = ppFromDate || 'all';
    const to = ppToDate || 'all';
    const filename = `pending_processes_${axis}_${from}_to_${to}.xlsx`;
    exportToExcel(rows, 'Pending Processes', filename);
  };

  useEffect(() => {
    fetchData();
  }, [weekNumber, weekYear]);

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchWeeklyReport();
    }
  }, [activeTab, weekNumber, weekYear, filterSupervisor, filterWorker]);

  useEffect(() => {
    if (activeTab === 'planning') {
      fetchProcessSchedule();
    }
  }, [activeTab, weekNumber, weekYear, filterSupervisor, filterWorker, filterStatus, filterProcess, viewBy]);

  // F3 — lazy-load filter option lists once, on first activation of the tab.
  useEffect(() => {
    if (activeTab === 'pending_processes' && ppCustomers.length === 0 && ppSalesOrders.length === 0) {
      fetchPendingFilterOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // F3 — debounced refetch on sub-tab OR filter change (300ms, mirrors SOFulfillmentPage.js:1047-1053 idiom).
  useEffect(() => {
    if (activeTab !== 'pending_processes') return;
    const t = setTimeout(() => { fetchPendingProcesses(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pendingSubTab, ppFromDate, ppToDate, ppProcessFilter, ppCustomerFilter, ppSoFilter, ppItemFilter]);

  // Enhancement 2: Check process dependencies when WOs are selected
  useEffect(() => {
    const checkDependencies = async () => {
      const selectedItems = Object.values(selectedWOs);
      if (selectedItems.length === 0) {
        setDependencyWarnings([]);
        return;
      }
      
      setCheckingDependencies(true);
      try {
        const items = selectedItems.map(wo => ({
          work_order_id: wo.work_order_id,
          process_id: wo.process_id,
          planned_quantity: wo.planned_quantity || wo.remaining_quantity
        }));
        
        const response = await axios.post(`${API_URL}/production-plans/check-dependencies`, items, {
          headers: getAuthHeader()
        });
        
        setDependencyWarnings(response.data.warnings || []);
      } catch (error) {
        console.error('Error checking dependencies:', error);
        // Don't block the user - just log the error
      } finally {
        setCheckingDependencies(false);
      }
    };
    
    // Debounce the check to avoid too many API calls
    const timeoutId = setTimeout(checkDependencies, 500);
    return () => clearTimeout(timeoutId);
  }, [selectedWOs]);

  const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const handleThisWeek = () => setCurrentDate(new Date());

  // Handle process selection
  const handleProcessSelect = (processName) => {
    const proc = availableProcesses.find(p => p.process_name === processName);
    setSelectedProcess(proc);
    setSelectedWOs({});
  };

  // Toggle WO selection
  const toggleWOSelection = (wo) => {
    const key = `${wo.work_order_id}|${wo.process_id}`;
    setSelectedWOs(prev => {
      if (prev[key]) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      // Phase 1 (Step 17): pre-fill required start + completion dates from the plan's week.
      // Phase 1 (Step 18): plannable_qty (not remaining) caps the planned input.
      const defaultStart = weekDates[0]?.toISOString().split('T')[0] || '';
      const defaultEnd = weekDates[weekDates.length - 1]?.toISOString().split('T')[0] || '';
      return {
        ...prev,
        [key]: {
          ...wo,
          planned_quantity: wo.plannable_qty ?? wo.remaining_quantity,
          planned_start_date: defaultStart,
          planned_completion_date: defaultEnd
        }
      };
    });
  };

  // Update planned quantity for a WO
  const updatePlannedQty = (key, qty) => {
    setSelectedWOs(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        planned_quantity: parseFloat(qty) || 0
      }
    }));
  };

  // Phase 1 (Step 17): update planned start date
  const updatePlannedStartDate = (key, date) => {
    setSelectedWOs(prev => ({
      ...prev,
      [key]: { ...prev[key], planned_start_date: date }
    }));
  };

  // Update planned completion date
  const updatePlannedDate = (key, date) => {
    setSelectedWOs(prev => {
      const wo = prev[key];
      const benchmarkDate = wo?.benchmark_date ? new Date(wo.benchmark_date) : null;
      const plannedDate = date ? new Date(date) : null;
      
      // Calculate delay days
      let delayDays = 0;
      if (benchmarkDate && plannedDate) {
        delayDays = Math.max(0, Math.ceil((plannedDate - benchmarkDate) / (1000 * 60 * 60 * 24)));
      }
      
      return {
        ...prev,
        [key]: {
          ...prev[key],
          planned_completion_date: date,
          delay_days: delayDays
        }
      };
    });
  };

  // Generate dates for the current week
  const getWeekDates = () => {
    const dates = [];
    const startDate = new Date(weekStart);
    for (let i = 0; i < 6; i++) { // Monday to Saturday
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  // Toggle worker selection
  const toggleWorker = (workerId) => {
    setWorkerIds(prev =>
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!selectedProcess || Object.keys(selectedWOs).length === 0 || !supervisorId) {
      toast.error('Please select a process, at least one WO, and a supervisor');
      return;
    }

    // Phase 1 (Step 17 + 18): per-item validation BEFORE submit (mirrors backend Steps 2/3/4 errors).
    const woRowByKey = {};
    for (const wo of (selectedProcess.work_orders || [])) {
      woRowByKey[`${wo.work_order_id}|${wo.process_id}`] = wo;
    }
    for (const wo of Object.values(selectedWOs)) {
      const woNum = wo.work_order_number || wo.work_order_id;
      if (!wo.planned_quantity || wo.planned_quantity <= 0) {
        toast.error(`Cannot plan zero quantity for WO ${woNum}. Remove the item or set a positive quantity.`);
        return;
      }
      if (!wo.planned_start_date) {
        toast.error(`Plan start date required for WO ${woNum}`);
        return;
      }
      if (!wo.planned_completion_date) {
        toast.error(`Plan completion date required for WO ${woNum}`);
        return;
      }
      if (new Date(wo.planned_start_date) > new Date(wo.planned_completion_date)) {
        toast.error(`Start date cannot be after completion date for WO ${woNum}`);
        return;
      }
      const row = woRowByKey[`${wo.work_order_id}|${wo.process_id}`];
      // Edit mode: row may be missing for items already in the plan; only enforce in Create mode where row exists.
      if (row && row.plannable_qty !== undefined && wo.planned_quantity > row.plannable_qty) {
        toast.error(`Cannot plan more than ${row.plannable_qty} for ${woNum} (remaining minus already-committed across other active plans)`);
        return;
      }
    }

    try {
      const planned_items = Object.values(selectedWOs).map(wo => {
        const row = woRowByKey[`${wo.work_order_id}|${wo.process_id}`];
        // Convert YYYY-MM-DD -> YYYY-MM-DDT00:00:00 ISO (datetime expected by backend Pydantic models).
        const toIso = (d) => (d && d.length === 10 ? `${d}T00:00:00` : d);
        return {
          work_order_id: wo.work_order_id,
          process_id: wo.process_id,
          process_master_id: wo.process_master_id || row?.process_master_id || selectedProcess.process_master_id || null,
          planned_quantity: wo.planned_quantity,
          benchmark_date: wo.benchmark_date || null,
          planned_start_date: toIso(wo.planned_start_date),
          planned_completion_date: toIso(wo.planned_completion_date)
        };
      });

      if (editPlan) {
        // EDIT path -> PATCH existing plan
        const patchPayload = {
          planned_items,
          supervisor_id: supervisorId,
          employee_ids: workerIds
        };
        await axios.patch(`${API_URL}/production-plans/${editPlan.id}`, patchPayload, { headers: getAuthHeader() });
        toast.success('Production plan updated successfully');
      } else {
        const payload = {
          week_number: weekNumber,
          week_year: weekYear,
          process_name: selectedProcess.process_name,
          process_master_id: selectedProcess.process_master_id,
          planned_items,
          supervisor_id: supervisorId,
          employee_ids: workerIds
        };
        await axios.post(`${API_URL}/production-plans`, payload, { headers: getAuthHeader() });
        toast.success('Production plan created successfully');
      }
      setOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      // Handle BOTH 422 (Pydantic structured field errors) AND 400 (flat detail string).
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic structured: [{loc:[...], msg:"...", type:"..."}]
        const first = detail[0];
        const path = (first?.loc || []).slice(-2).join('.');
        toast.error(`${path}: ${first?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error(editPlan ? 'Failed to update plan' : 'Failed to create plan');
      }
    }
  };

  const resetForm = () => {
    setSelectedProcess(null);
    setSelectedWOs({});
    setSupervisorId('');
    setWorkerIds([]);
    setWorkerSearch('');
    setEditPlan(null);
    setDependencyWarnings([]);  // Enhancement 2: Clear warnings
  };

  const handleEdit = async (plan) => {
    setEditPlan(plan);
    
    // Fetch available WOs for this process to populate the table
    try {
      // Phase 1 (Step 19): exclude this plan's commitments from plannable_qty math.
      const response = await axios.get(
        `${API_URL}/production-plans/available-processes?exclude_plan_id=${encodeURIComponent(plan.id)}`,
        { headers: getAuthHeader() }
      );
      
      // Find the matching process group.
      // Strict master-id match when both sides have it; fall back to trim+lowercase name match.
      const planMasterId = plan.process_master_id;
      const planNameNorm = (plan.process_name || '').trim().toLowerCase();
      const matchingProcess = response.data.find(p => {
        const groupMasterId = p.process_master_id;
        if (planMasterId && groupMasterId) {
          return groupMasterId === planMasterId;
        }
        const groupNameNorm = (p.process_name || '').trim().toLowerCase();
        return groupNameNorm === planNameNorm;
      });
      
      // Build work_orders array from:
      // 1. Available WOs from API
      // 2. PLUS the existing planned items (so they show as selected)
      let workOrders = matchingProcess?.work_orders || [];
      
      // Add existing planned items if not already in the list (they might have 0 remaining)
      (plan.planned_items || []).forEach(item => {
        const exists = workOrders.find(wo => 
          wo.work_order_id === item.work_order_id && wo.process_id === item.process_id
        );
        if (!exists) {
          workOrders.push({
            work_order_id: item.work_order_id,
            work_order_number: item.work_order_number,
            process_id: item.process_id,
            process_master_id: item.process_master_id || matchingProcess?.process_master_id || plan.process_master_id || null,
            item_name: item.item_name,
            remaining_quantity: item.planned_quantity, // Use planned as remaining for display
            benchmark_date: item.benchmark_date
          });
        }
      });
      
      setSelectedProcess({ 
        process_name: plan.process_name, 
        process_master_id: plan.process_master_id || matchingProcess?.process_master_id || null, 
        work_orders: workOrders 
      });
    } catch (error) {
      console.error('Error fetching processes for edit:', error);
      // Fallback: create work_orders from planned_items
      const workOrders = (plan.planned_items || []).map(item => ({
        work_order_id: item.work_order_id,
        work_order_number: item.work_order_number,
        process_id: item.process_id,
        process_master_id: item.process_master_id || plan.process_master_id || null,
        item_name: item.item_name,
        remaining_quantity: item.planned_quantity,
        benchmark_date: item.benchmark_date
      }));
      setSelectedProcess({ 
        process_name: plan.process_name, 
        process_master_id: plan.process_master_id, 
        work_orders: workOrders 
      });
    }
    
    // Populate selected WOs with existing plan data
    const defaultStart = weekDates[0]?.toISOString().split('T')[0] || '';
    const defaultEnd = weekDates[weekDates.length - 1]?.toISOString().split('T')[0] || '';
    const isoToDate = (v) => (v ? String(v).split('T')[0] : '');
    const woSelections = {};
    (plan.planned_items || []).forEach(item => {
      const key = `${item.work_order_id}|${item.process_id}`;
      woSelections[key] = {
        ...item,
        process_master_id: item.process_master_id || plan.process_master_id || null,
        planned_quantity: item.planned_quantity,
        // Phase 1 (Step 17): pre-fill from existing values; fall back to plan's week bounds.
        planned_start_date: isoToDate(item.planned_start_date) || defaultStart,
        planned_completion_date: isoToDate(item.planned_completion_date) || defaultEnd,
        checked: true
      };
    });
    setSelectedWOs(woSelections);
    setSupervisorId(plan.supervisor_id);
    setWorkerIds(plan.worker_ids || plan.employee_ids || []);
    setOpen(true);
  };

  const handleDelete = async (planId) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await axios.delete(`${API_URL}/production-plans/${planId}`, { headers: getAuthHeader() });
      toast.success('Plan deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plan');
    }
  };

  // Phase 1 (Step 14): Cancel a production plan (owner-only).
  const handleOpenCancel = (plan) => {
    setCancelPlan(plan);
    setCancelReason('');
    setCancelNotes('');
  };

  const handleCloseCancel = () => {
    setCancelPlan(null);
    setCancelReason('');
    setCancelNotes('');
    setCancelSubmitting(false);
  };

  const handleSubmitCancel = async () => {
    if (!cancelPlan || !cancelReason) {
      toast.error('Please select a reason code');
      return;
    }
    const trimmed = (cancelNotes || '').trim();
    if (cancelReason === 'other' && trimmed.length < 3) {
      toast.error('Notes are required (min 3 chars) when reason is "Other"');
      return;
    }
    if (trimmed.length > 500) {
      toast.error('Notes must be 500 characters or fewer');
      return;
    }
    setCancelSubmitting(true);
    try {
      const body = { reason_code: cancelReason };
      if (trimmed) body.notes = trimmed;
      await axios.post(
        `${API_URL}/production-plans/${cancelPlan.id}/cancel`,
        body,
        { headers: getAuthHeader() }
      );
      toast.success('Plan cancelled');
      handleCloseCancel();
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const first = detail[0];
        toast.error(first?.msg || 'Validation error');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to cancel plan');
      }
      setCancelSubmitting(false);
    }
  };
  const togglePlanExpand = (planId) => {
    setExpandedPlan(prev => prev === planId ? null : planId);
  };

  // Filter plans
  const filteredPlans = plans.filter(plan => {
    if (filterSupervisor !== 'all' && plan.supervisor_id !== filterSupervisor) return false;
    if (filterWorker !== 'all' && !plan.worker_ids?.includes(filterWorker)) return false;
    if (filterStatus !== 'all' && plan.status !== filterStatus) return false;
    return true;
  });

  // Calculate total selected qty
  const totalSelectedQty = Object.values(selectedWOs).reduce((sum, wo) => sum + (wo.planned_quantity || 0), 0);

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don&apos;t have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header with Week Navigator */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={handlePrevWeek}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center">
              <p className="font-semibold text-lg">
                Week {weekNumber}, {weekYear}
              </p>
              <p className="text-sm text-muted-foreground">
                {format(weekStart, 'dd/MM')} - {format(saturday, 'dd/MM/yyyy')}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleNextWeek}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleThisWeek}>
              Today
            </Button>
          </div>
          
          <div className="flex gap-2">
            {/* Create Plan Button */}
            <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Plan
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editPlan ? 'Edit' : 'Create'} Production Plan</DialogTitle>
                  <DialogDescription>
                    Plan production for Week {weekNumber} ({format(weekStart, 'dd/MM')} - {format(saturday, 'dd/MM')})
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate}>
                  <div className="space-y-6 py-4">
                    {/* Step 1: Select Process */}
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">Step 1: Select Process Type *</Label>
                      <Select
                        value={selectedProcess?.process_name || ''}
                        onValueChange={handleProcessSelect}
                        disabled={!!editPlan}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a process to plan..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableProcesses.map(proc => (
                            <SelectItem key={proc.process_name} value={proc.process_name}>
                              <div className="flex items-center gap-2">
                                <Settings className="w-4 h-4 text-purple-600" />
                                {proc.process_name}
                                <Badge variant="outline" className="ml-2">
                                  {proc.work_orders.length} WO(s)
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Step 2: Select Work Orders */}
                    {selectedProcess && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-semibold">Step 2: Select Work Orders</Label>
                          <Badge variant="secondary">
                            {Object.keys(selectedWOs).length} selected | Total: {totalSelectedQty} units
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Sorted by Benchmark Date (earliest first) - plan urgent orders first
                        </p>
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/50">
                                <TableHead className="w-12">Select</TableHead>
                                <TableHead>WO Number</TableHead>
                                <TableHead>Item</TableHead>
                                <TableHead className="text-center">Benchmark Date</TableHead>
                                <TableHead className="text-center">Plan Start</TableHead>
                                <TableHead className="text-center">Plan Completion</TableHead>
                                <TableHead className="text-right">Required Qty</TableHead>
                                <TableHead className="text-right w-32">Planned Qty</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedProcess.work_orders.map((wo, idx) => {
                                const key = `${wo.work_order_id}|${wo.process_id}`;
                                const isSelected = !!selectedWOs[key];
                                const isOverdue = wo.benchmark_date && new Date(wo.benchmark_date) < new Date();
                                const isUrgent = wo.benchmark_date && !isOverdue && 
                                  (new Date(wo.benchmark_date) - new Date()) / (1000 * 60 * 60 * 24) <= 7;
                                const delayDays = selectedWOs[key]?.delay_days || 0;
                                const isAssemblyPacking = selectedProcess.process_name.toLowerCase().includes('assembly') || 
                                                          selectedProcess.process_name.toLowerCase().includes('packing') ||
                                                          selectedProcess.process_name.toLowerCase().includes('a&p');
                                
                                return (
                                  <TableRow 
                                    key={key} 
                                    className={`cursor-pointer hover:bg-muted/30 ${isSelected ? 'bg-primary/5' : ''} ${isOverdue ? 'bg-red-50' : isUrgent ? 'bg-orange-50' : ''}`}
                                    onClick={() => toggleWOSelection(wo)}
                                  >
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() => toggleWOSelection(wo)}
                                      />
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">{wo.work_order_number}</TableCell>
                                    <TableCell>{wo.item_name}</TableCell>
                                    <TableCell className="text-center">
                                      {wo.benchmark_date ? (
                                        <div className="flex items-center justify-center gap-1">
                                          <Target className={`w-3 h-3 ${isOverdue ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-muted-foreground'}`} />
                                          <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : isUrgent ? 'text-orange-600 font-medium' : ''}`}>
                                            {format(new Date(wo.benchmark_date), 'dd/MM')}
                                          </span>
                                          {isOverdue && <Badge variant="destructive" className="ml-1 text-xs py-0">Overdue</Badge>}
                                          {isUrgent && !isOverdue && <Badge className="ml-1 text-xs py-0 bg-orange-500">Urgent</Badge>}
                                        </div>
                                      ) : (
                                        <span className="text-muted-foreground text-sm">Not set</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                      {isSelected && (
                                        <Select
                                          value={selectedWOs[key]?.planned_start_date || ''}
                                          onValueChange={(date) => updatePlannedStartDate(key, date)}
                                        >
                                          <SelectTrigger className="w-28 h-8 text-xs" data-testid={`plan-start-date-${wo.work_order_id}`}>
                                            <SelectValue placeholder="Select" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {weekDates.map((date) => (
                                              <SelectItem key={date.toISOString()} value={date.toISOString().split('T')[0]}>
                                                {format(date, 'EEE dd/MM')}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                      {isSelected && (
                                        <div className="flex flex-col items-center gap-1">
                                          <Select
                                            value={selectedWOs[key]?.planned_completion_date || ''}
                                            onValueChange={(date) => updatePlannedDate(key, date)}
                                          >
                                            <SelectTrigger className="w-28 h-8 text-xs" data-testid={`plan-completion-date-${wo.work_order_id}`}>
                                              <SelectValue placeholder="Select date" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {weekDates.map((date) => (
                                                <SelectItem key={date.toISOString()} value={date.toISOString().split('T')[0]}>
                                                  {format(date, 'EEE dd/MM')}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          {delayDays > 0 && (
                                            <Badge variant="outline" className={`text-xs ${isAssemblyPacking ? 'bg-red-100 text-red-700 border-red-300' : 'bg-yellow-100 text-yellow-700 border-yellow-300'}`}>
                                              +{delayDays}d {isAssemblyPacking && '⚠️'}
                                            </Badge>
                                          )}
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                      {/* Phase 1 (Step 18): show plannable_qty inline when other plans have committed capacity. */}
                                      {(wo.plannable_qty !== undefined && wo.plannable_qty < wo.remaining_quantity) ? (
                                        <div className="flex flex-col items-end">
                                          <span>{wo.remaining_quantity}</span>
                                          <span
                                            className="text-xs text-muted-foreground"
                                            title="After commitments from other active plans"
                                            data-testid={`plannable-qty-${wo.work_order_id}`}
                                          >
                                            Plannable: {wo.plannable_qty}
                                          </span>
                                        </div>
                                      ) : (
                                        <span>{wo.remaining_quantity}</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                      {isSelected && (
                                        <Input
                                          type="number"
                                          className="w-24 h-8 text-right"
                                          value={selectedWOs[key]?.planned_quantity || ''}
                                          onChange={(e) => updatePlannedQty(key, e.target.value)}
                                          min={0}
                                          max={wo.plannable_qty ?? wo.remaining_quantity}
                                          data-testid={`planned-qty-input-${wo.work_order_id}`}
                                        />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                        
                        {/* Delay Warning */}
                        {(() => {
                          const delayedItems = Object.values(selectedWOs).filter(wo => wo.delay_days > 0);
                          const isAssemblyPacking = selectedProcess?.process_name?.toLowerCase().includes('assembly') || 
                                                    selectedProcess?.process_name?.toLowerCase().includes('packing') ||
                                                    selectedProcess?.process_name?.toLowerCase().includes('a&p');
                          
                          if (delayedItems.length === 0) return null;
                          
                          return (
                            <div className={`mt-3 p-3 rounded-lg border ${isAssemblyPacking ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                              <div className="flex items-start gap-2">
                                <span className="text-lg">⚠️</span>
                                <div>
                                  <p className={`font-medium ${isAssemblyPacking ? 'text-red-700' : 'text-yellow-700'}`}>
                                    {delayedItems.length} WO(s) planned beyond benchmark date
                                  </p>
                                  {isAssemblyPacking && (
                                    <p className="text-sm text-red-600 mt-1">
                                      <strong>Assembly & Packing delay detected!</strong> Sales team will be notified of the revised delivery dates.
                                    </p>
                                  )}
                                  {!isAssemblyPacking && (
                                    <p className="text-sm text-yellow-600 mt-1">
                                      This may affect downstream processes and final delivery dates.
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Enhancement 2: Process Dependency Warnings */}
                        {dependencyWarnings.length > 0 && (
                          <div className="mt-3 p-3 rounded-lg border bg-orange-50 border-orange-200">
                            <div className="flex items-start gap-2">
                              <span className="text-lg">🔗</span>
                              <div className="flex-1">
                                <p className="font-medium text-orange-700">
                                  Predecessor Process Incomplete ({dependencyWarnings.length} item{dependencyWarnings.length !== 1 ? 's' : ''})
                                </p>
                                <div className="mt-2 space-y-1">
                                  {dependencyWarnings.map((warning, idx) => (
                                    <div key={idx} className="text-sm text-orange-600 flex items-start gap-1">
                                      <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${warning.severity === 'high' ? 'bg-red-500' : 'bg-orange-400'}`}></span>
                                      <span>
                                        <strong>{warning.work_order_number}</strong>: {warning.message}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                <p className="text-xs text-orange-500 mt-2 italic">
                                  This is a non-blocking warning. You may still proceed with planning.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        {checkingDependencies && (
                          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Checking dependencies...
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step 3: Assign Supervisor and Workers */}
                    {Object.keys(selectedWOs).length > 0 && (
                      <div className="space-y-4">
                        <Label className="text-base font-semibold">Step 3: Assign Team</Label>
                        
                        <div className="grid grid-cols-2 gap-4">
                          {/* Supervisor */}
                          <div className="space-y-2">
                            <Label>Supervisor *</Label>
                            <SearchableSelect
                              options={employees.map(emp => ({
                                value: emp.id,
                                label: `${emp.name}${emp.designation ? ` (${emp.designation})` : ''}`
                              }))}
                              value={supervisorId}
                              onValueChange={setSupervisorId}
                              placeholder="Select supervisor..."
                              searchPlaceholder="Type to search employees..."
                              emptyText="No employees found"
                            />
                          </div>

                          {/* Workers - Show only Shopfloor Staff */}
                          <div className="space-y-2">
                            <Label>Assign Workers ({workerIds.length} selected) - Shopfloor Staff Only</Label>

                            {/* Selected workers chip strip — preserves selection order; click ✕ to deselect. */}
                            {workerIds.length > 0 && (
                              <div
                                className="flex flex-wrap gap-1.5 rounded-lg border bg-muted/30 p-2"
                                data-testid="selected-workers-box"
                              >
                                {workerIds.map((wid) => {
                                  const emp = employees.find(e => e.id === wid);
                                  const label = emp ? emp.name : wid;
                                  const desig = emp?.designation;
                                  return (
                                    <span
                                      key={wid}
                                      className="inline-flex items-center gap-1.5 rounded-full bg-background border pl-2 pr-1 py-0.5 text-xs shadow-sm"
                                      data-testid={`selected-worker-chip-${wid}`}
                                    >
                                      <span className="font-medium">{label}</span>
                                      {desig && <span className="text-muted-foreground">· {desig}</span>}
                                      <button
                                        type="button"
                                        onClick={() => toggleWorker(wid)}
                                        className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                                        aria-label={`Remove ${label}`}
                                        data-testid={`remove-worker-chip-${wid}`}
                                      >
                                        <XCircle className="h-3.5 w-3.5" />
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            )}

                            <Input
                              type="text"
                              placeholder="Search workers by name or designation..."
                              value={workerSearch}
                              onChange={(e) => setWorkerSearch(e.target.value)}
                              className="h-9"
                              data-testid="worker-search-input"
                            />
                            <div className="border rounded-lg p-3 max-h-32 overflow-y-auto">
                              <div className="grid grid-cols-1 gap-2">
                                {(() => {
                                  const q = workerSearch.trim().toLowerCase();
                                  const filtered = employees
                                    .filter(emp => emp.staff_type === 'Shopfloor Staff')
                                    .filter(emp => !q || (emp.name || '').toLowerCase().includes(q) || (emp.designation || '').toLowerCase().includes(q));
                                  if (filtered.length === 0) {
                                    return <p className="text-sm text-muted-foreground py-2">No workers match "{workerSearch}"</p>;
                                  }
                                  return filtered.map(emp => (
                                  <label
                                    key={emp.id}
                                    htmlFor={`worker-${emp.id}`}
                                    className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted ${
                                      workerIds.includes(emp.id) ? 'bg-primary/10' : ''
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      id={`worker-${emp.id}`}
                                      checked={workerIds.includes(emp.id)}
                                      onChange={() => toggleWorker(emp.id)}
                                      className="h-4 w-4 rounded border-gray-300"
                                    />
                                    <span className="text-sm">{emp.name}</span>
                                    {emp.designation && <Badge variant="outline" className="text-xs">{emp.designation}</Badge>}
                                  </label>
                                  ));
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit"
                      disabled={!selectedProcess || Object.keys(selectedWOs).length === 0 || !supervisorId}
                    >
                      {editPlan ? 'Update' : 'Create'} Plan
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="planning">
              <ClipboardList className="w-4 h-4 mr-2" />
              Planning Register
            </TabsTrigger>
            <TabsTrigger value="reports">
              <BarChart3 className="w-4 h-4 mr-2" />
              Weekly Report
            </TabsTrigger>
            <TabsTrigger value="pending_processes" data-testid="pending-processes-tab-trigger">
              <Clock className="w-4 h-4 mr-2" />
              Pending Processes
            </TabsTrigger>
          </TabsList>

          {/* Planning Register Tab */}
          <TabsContent value="planning" className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm">Supervisor:</Label>
                <Select value={filterSupervisor} onValueChange={setFilterSupervisor}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Worker:</Label>
                <Select value={filterWorker} onValueChange={setFilterWorker}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Status:</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="partially_completed">Partially Completed</SelectItem>
                    <SelectItem value="rolled_over">Rolled Over (Legacy)</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="unplanned">Unplanned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Process:</Label>
                <Select value={filterProcess} onValueChange={setFilterProcess}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Processes</SelectItem>
                    {(processSchedule?.distinct_processes || []).map(proc => (
                      <SelectItem key={proc} value={proc}>{proc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Label className="text-sm">View By:</Label>
                <div className="flex border rounded-md">
                  <Button
                    variant={viewBy === 'planned' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-r-none"
                    onClick={() => setViewBy('planned')}
                  >
                    Planned Date
                  </Button>
                  <Button
                    variant={viewBy === 'benchmark' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-l-none"
                    onClick={() => setViewBy('benchmark')}
                  >
                    Benchmark Date
                  </Button>
                </div>
              </div>
            </div>

            {/* Process Schedule Summary */}
            {processSchedule && (
              <div className="grid grid-cols-6 gap-2 text-sm">
                <div className="bg-muted/50 rounded p-2 text-center">
                  <div className="font-semibold">{processSchedule.summary?.total || 0}</div>
                  <div className="text-muted-foreground text-xs">Total</div>
                </div>
                <div className="bg-blue-50 rounded p-2 text-center">
                  <div className="font-semibold text-blue-600">{processSchedule.summary?.planned || 0}</div>
                  <div className="text-muted-foreground text-xs">Planned</div>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="font-semibold text-gray-600">{processSchedule.summary?.unplanned || 0}</div>
                  <div className="text-muted-foreground text-xs">Unplanned</div>
                </div>
                <div className="bg-green-50 rounded p-2 text-center">
                  <div className="font-semibold text-green-600">{processSchedule.summary?.on_track || 0}</div>
                  <div className="text-muted-foreground text-xs">On Track</div>
                </div>
                <div className="bg-yellow-50 rounded p-2 text-center">
                  <div className="font-semibold text-yellow-600">{processSchedule.summary?.at_risk || 0}</div>
                  <div className="text-muted-foreground text-xs">At Risk</div>
                </div>
                <div className="bg-red-50 rounded p-2 text-center">
                  <div className="font-semibold text-red-600">{(processSchedule.summary?.delayed || 0) + (processSchedule.summary?.overdue || 0)}</div>
                  <div className="text-muted-foreground text-xs">Delayed</div>
                </div>
              </div>
            )}

            {/* Process Schedule Table */}
            {processSchedule && processSchedule.items && processSchedule.items.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>WO Number</TableHead>
                      <TableHead>Process</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className={viewBy === 'benchmark' ? 'bg-blue-50/50' : ''}>Benchmark Date</TableHead>
                      <TableHead className={viewBy === 'planned' ? 'bg-blue-50/50' : ''}>Planned Date</TableHead>
                      <TableHead className="text-center">Variance</TableHead>
                      <TableHead>Supervisor</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {processSchedule.items.map((item, idx) => {
                      const varianceClass = item.variance_days === null ? 'text-gray-400' :
                        item.variance_days <= 0 ? 'text-green-600' :
                        item.variance_days <= 3 ? 'text-yellow-600' : 'text-red-600';
                      
                      const statusBadge = {
                        'on_track': { bg: 'bg-green-100', text: 'text-green-700', label: 'On Track' },
                        'at_risk': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'At Risk' },
                        'delayed': { bg: 'bg-red-100', text: 'text-red-700', label: 'Delayed' },
                        'overdue': { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
                        'completed': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Completed' },
                        'unplanned': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Unplanned' }
                      }[item.risk_status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: item.risk_status };
                      
                      return (
                        <TableRow key={`${item.work_order_id}-${item.process_id}-${idx}`} className="hover:bg-muted/30">
                          <TableCell className="font-medium">{item.work_order_number}</TableCell>
                          <TableCell>{item.process_name}</TableCell>
                          <TableCell className="text-right">{item.remaining_quantity}</TableCell>
                          <TableCell className={viewBy === 'benchmark' ? 'bg-blue-50/30 font-medium' : ''}>
                            {item.benchmark_date ? new Date(item.benchmark_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                          </TableCell>
                          <TableCell className={viewBy === 'planned' ? 'bg-blue-50/30 font-medium' : ''}>
                            {item.planned_date ? new Date(item.planned_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                          </TableCell>
                          <TableCell className={`text-center font-medium ${varianceClass}`}>
                            {item.variance_days === null ? '-' : 
                              item.variance_days === 0 ? '0' :
                              item.variance_days > 0 ? `+${item.variance_days}d` : `${item.variance_days}d`}
                          </TableCell>
                          <TableCell>{item.supervisor_name || '-'}</TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Existing Plans Table */}
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Process</TableHead>
                    <TableHead className="text-center">WOs</TableHead>
                    <TableHead className="text-right">Planned</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-center w-32">Progress</TableHead>
                    <TableHead>Supervisor</TableHead>
                    <TableHead>Workers</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPlans.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12">
                        <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground">No plans for this week</p>
                        <p className="text-sm text-muted-foreground mt-1">Create a plan to get started</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPlans.map((plan) => {
                      const plannedItems = plan.planned_items || [];
                      const hasMultipleWOs = plannedItems.length > 1;
                      const isExpanded = expandedPlan === plan.id;
                      const progress = plan.planned_quantity > 0 
                        ? Math.min((plan.actual_quantity || 0) / plan.planned_quantity * 100, 100) 
                        : 0;
                      
                      return (
                        <React.Fragment key={plan.id}>
                          <TableRow className="hover:bg-muted/30">
                            <TableCell>
                              {plannedItems.length > 0 && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="p-1 h-6 w-6"
                                  onClick={() => togglePlanExpand(plan.id)}
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Settings className="w-4 h-4 text-purple-600" />
                                <span className="font-medium">{plan.process_name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">
                                {plannedItems.length || 1} WO{(plannedItems.length || 1) !== 1 ? 's' : ''}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium">{plan.planned_quantity}</TableCell>
                            <TableCell className="text-right font-medium text-green-600">
                              {plan.actual_quantity || 0}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress value={progress} className="h-2" />
                                <span className="text-xs text-muted-foreground w-10">{progress.toFixed(0)}%</span>
                              </div>
                            </TableCell>
                            <TableCell>{plan.supervisor_name}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {plan.worker_names?.slice(0, 2).map((name, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">{name}</Badge>
                                ))}
                                {plan.worker_names?.length > 2 && (
                                  <Badge variant="outline" className="text-xs">+{plan.worker_names.length - 2}</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={statusColors[plan.status]}>
                                {plan.status.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {(() => {
                                  // Phase 1 (Steps 14, 15, 16): owner check + state guards.
                                  // Canonical: relies on `is_owner` from /api/auth/me (set for all 4 owners
                                  // — Dinesh, Raj, Prachi, Preksha — by routes/auth.py). Mirrors the
                                  // pattern used by 14 other frontend pages (ChecklistPage, DelegationPage, etc).
                                  const isOwner = user?.is_owner === true;
                                  const TERMINAL = ['completed', 'partially_completed', 'cancelled', 'rolled_over'];
                                  const isTerminal = TERMINAL.includes(plan.status);
                                  const isPlanned = plan.status === 'planned';
                                  const hasActuals = (plan.planned_items || []).some(it => (it.actual_quantity || 0) > 0)
                                    || (plan.actual_quantity || 0) > 0;
                                  const canDelete = isPlanned && !hasActuals;
                                  return (
                                    <>
                                      {/* Cancel: owner-only render; only visible while plan is non-terminal (Step 14) */}
                                      {isOwner && !isTerminal && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleOpenCancel(plan)}
                                          title="Cancel plan"
                                          data-testid={`cancel-plan-btn-${plan.id}`}
                                        >
                                          <XCircle className="w-4 h-4 text-destructive" />
                                        </Button>
                                      )}
                                      {/* Edit: terminal-state plans cannot be edited (Step 16) */}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEdit(plan)}
                                        disabled={isTerminal}
                                        title={isTerminal ? `Cannot edit a plan in ${plan.status} state.` : 'Edit plan'}
                                        data-testid={`edit-plan-btn-${plan.id}`}
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                      {/* Delete: owner-only render; disabled when not planned or has actuals (Step 15) */}
                                      {isOwner && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleDelete(plan.id)}
                                          disabled={!canDelete}
                                          title={canDelete ? 'Delete plan' : 'Cannot delete plans with production progress. Use Cancel instead.'}
                                          data-testid={`delete-plan-btn-${plan.id}`}
                                        >
                                          <Trash2 className="w-4 h-4 text-destructive" />
                                        </Button>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded WO Items */}
                          {isExpanded && plannedItems.map((item, idx) => {
                            const itemProgress = item.planned_quantity > 0
                              ? Math.min((item.actual_quantity || 0) / item.planned_quantity * 100, 100)
                              : 0;
                            return (
                              <TableRow key={`${plan.id}-${idx}`} className="bg-muted/20 text-sm">
                                <TableCell></TableCell>
                                <TableCell colSpan={2} className="pl-10">
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">↳</span>
                                    <span className="font-mono">{item.work_order_number}</span>
                                    <span className="text-muted-foreground">-</span>
                                    <span>{item.item_name}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">{item.planned_quantity}</TableCell>
                                <TableCell className="text-right text-green-600">{item.actual_quantity || 0}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Progress value={itemProgress} className="h-1.5" />
                                    <span className="text-xs text-muted-foreground w-10">{itemProgress.toFixed(0)}%</span>
                                  </div>
                                </TableCell>
                                <TableCell colSpan={4}>
                                  {item.benchmark_date && (
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Target className="w-3 h-3" />
                                      {format(new Date(item.benchmark_date), 'dd/MM')}
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Weekly Report Tab */}
          <TabsContent value="reports" className="space-y-6">
            {weeklyReport ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Total Plans</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{weeklyReport.total_plans}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Planned Qty</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{weeklyReport.total_planned}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Actual Qty</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-600">{weeklyReport.total_actual}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Overall Efficiency</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={`text-3xl font-bold ${weeklyReport.overall_efficiency >= 80 ? 'text-green-600' : weeklyReport.overall_efficiency >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {weeklyReport.overall_efficiency}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Report Tables */}
                <div className="grid grid-cols-3 gap-6">
                  {/* By Process */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        By Process
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {weeklyReport.by_process.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm truncate flex-1">{item.process}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{item.actual}/{item.planned}</span>
                              <Badge variant={item.efficiency >= 80 ? 'default' : 'secondary'} className="w-14 justify-center">
                                {item.efficiency}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                        {weeklyReport.by_process.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* By Supervisor */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        By Supervisor
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {weeklyReport.by_supervisor.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm truncate flex-1">{item.supervisor}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{item.actual}/{item.planned}</span>
                              <Badge variant={item.efficiency >= 80 ? 'default' : 'secondary'} className="w-14 justify-center">
                                {item.efficiency}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                        {weeklyReport.by_supervisor.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* By Worker */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        By Worker
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {weeklyReport.by_worker.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm truncate flex-1">{item.worker}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{item.actual}/{item.planned}</span>
                              <Badge variant={item.efficiency >= 80 ? 'default' : 'secondary'} className="w-14 justify-center">
                                {item.efficiency}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                        {weeklyReport.by_worker.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Loading report...</p>
              </div>
            )}
          </TabsContent>

          {/* F3 — Pending Processes Tab (read-only) */}
          <TabsContent value="pending_processes" className="space-y-4" data-testid="pending-processes-tab-content">
            <Tabs value={pendingSubTab} onValueChange={(v) => { setPendingSubTab(v); setExpandedProcessKey(null); }}>
              <TabsList data-testid="pending-processes-subtabs">
                <TabsTrigger value="so_target" data-testid="pp-subtab-so-target">By SO Target Date</TabsTrigger>
                <TabsTrigger value="benchmark" data-testid="pp-subtab-benchmark">By Process Benchmark Date</TabsTrigger>
              </TabsList>

              {/* Shared filters bar */}
              <div className="flex flex-wrap gap-3 items-end mt-4" data-testid="pp-filters">
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <Input type="date" value={ppFromDate} onChange={(e) => setPpFromDate(e.target.value)} className="w-40" data-testid="pp-from-date" />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <Input type="date" value={ppToDate} onChange={(e) => setPpToDate(e.target.value)} className="w-40" data-testid="pp-to-date" />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-muted-foreground">Process</Label>
                  <Select value={ppProcessFilter} onValueChange={setPpProcessFilter}>
                    <SelectTrigger className="w-44" data-testid="pp-process-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Processes</SelectItem>
                      {ppProcessMasters.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name || p.process_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-muted-foreground">Customer</Label>
                  <Select value={ppCustomerFilter} onValueChange={setPpCustomerFilter}>
                    <SelectTrigger className="w-44" data-testid="pp-customer-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {ppCustomers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-muted-foreground">Sales Order</Label>
                  <Select value={ppSoFilter} onValueChange={setPpSoFilter}>
                    <SelectTrigger className="w-44" data-testid="pp-so-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All SOs</SelectItem>
                      {ppSalesOrders.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.so_number}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs text-muted-foreground">Item</Label>
                  <Select value={ppItemFilter} onValueChange={setPpItemFilter}>
                    <SelectTrigger className="w-48" data-testid="pp-item-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Items</SelectItem>
                      {ppItems.map((it) => (
                        <SelectItem key={it.id} value={it.id}>{it.item_code ? `${it.item_code} — ${it.name}` : it.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={handleExportPendingProcesses} data-testid="pp-export-excel-btn">
                  <Download className="w-4 h-4 mr-2" /> Export to Excel
                </Button>
                <Button variant="ghost" onClick={() => {
                  setPpFromDate(''); setPpToDate(''); setPpProcessFilter('all');
                  setPpCustomerFilter('all'); setPpSoFilter('all'); setPpItemFilter('all');
                }} data-testid="pp-reset-filters-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Reset
                </Button>
              </div>

              {/* Shared content renderer — both sub-tabs use the same shape; only date-axis differs. */}
              <TabsContent value={pendingSubTab} className="mt-4" forceMount>
                {pendingLoading ? (
                  <div className="text-center py-10 text-muted-foreground text-sm" data-testid="pp-loading">Loading…</div>
                ) : (
                  <>
                    <PendingProcessesTable
                      title={pendingSubTab === 'so_target' ? 'By SO Target Date' : 'By Process Benchmark Date'}
                      groups={pendingData.with_date || []}
                      expandedProcessKey={expandedProcessKey}
                      setExpandedProcessKey={setExpandedProcessKey}
                      testIdPrefix="pp-with-date"
                    />
                    {pendingSubTab === 'benchmark' && (pendingData.without_date || []).length > 0 && (
                      <div className="mt-8">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">No Benchmark Date Set</Badge>
                          <span className="text-xs text-muted-foreground">
                            These processes have no benchmark_date on the WO subdoc. They bypass the date-range filter.
                          </span>
                        </div>
                        <PendingProcessesTable
                          title="No Benchmark Date Set"
                          groups={pendingData.without_date || []}
                          expandedProcessKey={expandedProcessKey}
                          setExpandedProcessKey={setExpandedProcessKey}
                          testIdPrefix="pp-no-date"
                          hideDate
                        />
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>

        {/* Phase 1 (Step 14): Cancel Production Plan dialog (owner-only render) */}
        <Dialog open={cancelPlan !== null} onOpenChange={(val) => { if (!val) handleCloseCancel(); }}>
          <DialogContent className="sm:max-w-md" data-testid="cancel-plan-dialog">
            <DialogHeader>
              <DialogTitle>Cancel Production Plan</DialogTitle>
              <DialogDescription>
                This action records cancellation reason + per-item attribution and cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {cancelPlan && (
              <div className="space-y-4">
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  <div><span className="text-muted-foreground">Process:</span> <span className="font-medium">{cancelPlan.process_name}</span></div>
                  <div><span className="text-muted-foreground">Week:</span> {cancelPlan.week_year}-W{String(cancelPlan.week_number).padStart(2, '0')}</div>
                  <div><span className="text-muted-foreground">Items:</span> {(cancelPlan.planned_items || []).length}</div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cancel-reason">Reason</Label>
                  <Select value={cancelReason} onValueChange={setCancelReason}>
                    <SelectTrigger id="cancel-reason" data-testid="cancel-reason-select">
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="machine_breakdown">Machine Breakdown</SelectItem>
                      <SelectItem value="raw_material_shortage">Raw Material Shortage</SelectItem>
                      <SelectItem value="manpower_absence">Manpower Absence</SelectItem>
                      <SelectItem value="customer_paused">Customer Paused</SelectItem>
                      <SelectItem value="schedule_change">Schedule Change</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cancel-notes">
                    Notes {cancelReason === 'other' ? <span className="text-destructive">(required, min 3 chars)</span> : <span className="text-muted-foreground">(optional, max 500)</span>}
                  </Label>
                  <Textarea
                    id="cancel-notes"
                    placeholder={cancelReason === 'other' ? 'Describe the reason' : 'Optional notes'}
                    value={cancelNotes}
                    onChange={(e) => setCancelNotes(e.target.value)}
                    maxLength={500}
                    data-testid="cancel-notes-textarea"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={handleCloseCancel} disabled={cancelSubmitting} data-testid="cancel-dialog-close-btn">
                Close
              </Button>
              <Button
                variant="destructive"
                onClick={handleSubmitCancel}
                disabled={cancelSubmitting || !cancelReason}
                data-testid="cancel-dialog-submit-btn"
              >
                {cancelSubmitting ? 'Cancelling…' : 'Cancel Plan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// F3 — Pending Processes sub-component (read-only master-detail)
//
// Single-row accordion expansion. Mirrors the idiom used in
// SOFulfillmentPage.js (ChevronDown/ChevronRight + state-lifted
// expandedKey). Parent row shows process-level aggregates; child
// rows list every contributing WO.
// ============================================================
function PendingProcessesTable({ title, groups, expandedProcessKey, setExpandedProcessKey, testIdPrefix, hideDate }) {
  if (!groups || groups.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground text-sm" data-testid={`${testIdPrefix}-empty`}>
        No pending processes match the current filters.
      </div>
    );
  }
  const keyOf = (g) => g.process_id || `__name__:${(g.process_name || '').toLowerCase()}`;
  return (
    <div className="border rounded-md" data-testid={`${testIdPrefix}-container`}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"></TableHead>
            <TableHead>Process</TableHead>
            <TableHead className="text-right">Total Pending Qty</TableHead>
            <TableHead className="text-right">Open WOs</TableHead>
            {!hideDate && <TableHead>Earliest Required Date</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((g) => {
            const k = keyOf(g);
            const isOpen = expandedProcessKey === k;
            return (
              <React.Fragment key={k}>
                <TableRow
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setExpandedProcessKey(isOpen ? null : k)}
                  data-testid={`${testIdPrefix}-row-${k}`}
                >
                  <TableCell className="w-10">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                  </TableCell>
                  <TableCell className="font-medium">{g.process_name || '(unnamed)'}</TableCell>
                  <TableCell className="text-right font-mono">{g.total_pending_qty}</TableCell>
                  <TableCell className="text-right">{g.open_wo_count}</TableCell>
                  {!hideDate && (
                    <TableCell>
                      {g.earliest_required_date
                        ? new Date(g.earliest_required_date).toLocaleDateString()
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                </TableRow>
                {isOpen && (g.work_orders || []).map((w) => (
                  <TableRow key={`${k}::${w.wo_id}`} className="bg-muted/20" data-testid={`${testIdPrefix}-wo-${w.wo_id}`}>
                    <TableCell></TableCell>
                    <TableCell colSpan={hideDate ? 3 : 4}>
                      <div className="grid grid-cols-6 gap-3 text-xs">
                        <div><span className="text-muted-foreground">WO:</span> <span className="font-medium">{w.wo_number}</span></div>
                        <div><span className="text-muted-foreground">Item:</span> {w.item_name}</div>
                        <div className="text-right"><span className="text-muted-foreground">Pending:</span> <span className="font-mono">{w.pending_qty}</span></div>
                        <div><span className="text-muted-foreground">Required:</span> {w.required_date ? new Date(w.required_date).toLocaleDateString() : '—'}</div>
                        <div><span className="text-muted-foreground">Customer:</span> {w.customer_name || '—'}</div>
                        <div><span className="text-muted-foreground">SO:</span> {w.so_number || '—'}</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
